/**
 * The Inbox's small furniture — `docs/activity.md` §7.1, Boards 7, 8, 16:
 * the contact's initials, the *Attached to* chip, the segmented control at
 * the Inbox's three heights, the buttons, and the words a moment is said in.
 * Kept here once so the list, the thread and the phone cannot drift apart.
 */
import type { ReactNode } from 'react'
import {
  KIND_STYLES, listTimeWords, zonedLocal, type ConversationRow, type InboxChannel,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { avatarColours } from '../../components/primitives.js'
import { Glyph, type GlyphName } from '../glyphs.js'
import type { Copy } from '../new/address-box.js'
import { dayWords } from '../activity-page.js'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** *9:32 AM*. */
export function clockWords(minutes: number): string {
  const h = Math.floor(minutes / 60)
  return `${((h + 11) % 12) + 1}:${String(minutes % 60).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`
}

/** *10:00*, no AM/PM — the Next up tile. */
export function clockShort(minutes: number): string {
  const h = Math.floor(minutes / 60)
  return `${((h + 11) % 12) + 1}:${String(minutes % 60).padStart(2, '0')}`
}

export function addDays(day: string, days: number): string {
  const date = new Date(`${day}T12:00:00Z`)
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

const weekdayShort = (day: string): string => DAYS[new Date(`${day}T12:00:00Z`).getUTCDay()]!
export const monthDay = (day: string): string => {
  const date = new Date(`${day}T12:00:00Z`)
  return `${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}`
}

export const localOf = (iso: string, timeZone: string): { date: string; minutes: number } =>
  zonedLocal(new Date(iso), timeZone)

/** A row's time: *9:32 AM* today, *Yesterday*, *Mon*, *Oct 12* (§7.1). */
export function rowTimeWords(iso: string | null, today: string, timeZone: string): string {
  if (!iso) return ''
  return listTimeWords(localOf(iso, timeZone), today, {
    clock: clockWords, weekdayShort, monthDay, addDays,
  })
}

/** A thread's day line: *Today*, *Yesterday*, *Tue, Oct 20*. */
export function dayHeading(day: string, today: string): string {
  if (day === today) return 'Today'
  if (day === addDays(today, -1)) return 'Yesterday'
  return dayWords(day)
}

/** An email card's time: *9:14 AM*, *Yesterday 7:12 PM*, *Tue 3:30 PM*, *Oct 12 3:30 PM* (Board 8 A). */
export function cardTimeWords(iso: string, today: string, timeZone: string): string {
  const local = localOf(iso, timeZone)
  const clock = clockWords(local.minutes)
  if (local.date === today) return clock
  if (local.date === addDays(today, -1)) return `Yesterday ${clock}`
  if (local.date > addDays(today, -7)) return `${weekdayShort(local.date)} ${clock}`
  return `${monthDay(local.date)} ${clock}`
}

/** The channel's glyph in its kind colour — bubble for a text, envelope for an email. */
export function ChannelGlyph({ channel, size = 12 }: { channel: InboxChannel | null; size?: number }) {
  if (!channel) return null
  const look = KIND_STYLES[channel]
  return (
    <span className="inline-flex shrink-0" style={{ color: look.fg }} title={look.label}>
      <Glyph name={look.icon as GlyphName} size={size} stroke={2.2} />
    </span>
  )
}

/**
 * The contact's initials in the product's avatar colour — the one the stop
 * page, the record and every list give the same name (Aaron, step 28: one
 * colour per person); *??* on grey for somebody Rowted doesn't know (§7.1).
 */
export function Initials({ row, size = 34 }: { row: Pick<ConversationRow, 'initials' | 'colour' | 'unknown' | 'name'>; size?: number }) {
  const font = size >= 44 ? 15 : size >= 34 ? 11.6 : 10
  return (
    <span
      title={row.name}
      aria-hidden
      className={cn('inline-flex shrink-0 items-center justify-center rounded-full font-bold tracking-[.02em]',
        row.unknown && 'bg-line-soft text-text-muted')}
      style={{
        width: size, height: size, fontSize: font,
        ...(row.unknown ? {} : avatarColours(row.name)),
      }}
    >
      {row.initials || '??'}
    </span>
  )
}

const CHIP_GLYPH: Record<string, GlyphName> = {
  job: 'layers', property: 'pin', contact: 'user', account: 'building',
}

/** A job's chip says its number only — *J-3318*, not *J-3318 · Backyard patio…* (Board 7). */
export function chipLabel(type: string, label: string): string {
  return type === 'job' ? label.split(' · ')[0] ?? label : label
}

/** The 20 px *Attached to* chip — the row's one, and the thread head's (Boards 7, 8). */
export function RecordChip({ type, label }: { type: string; label: string }) {
  return (
    <span className="inline-flex h-[22px] max-w-full shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-line-faint bg-surface-alt px-[7px] text-[11px] font-semibold text-text-body">
      <span className="inline-flex text-text-muted"><Glyph name={CHIP_GLYPH[type] ?? 'link'} size={11} /></span>
      <span className="truncate">{chipLabel(type, label)}</span>
    </span>
  )
}

/** A segmented control at the Inbox's heights: 24 (the toolbar), 22 (the composer), 26 (the phone). */
export function Seg<T extends string>({
  options, value, onChange, height = 24, label,
}: {
  options: readonly { value: T; label: string }[]
  value: T
  onChange: (value: T) => void
  height?: 22 | 24 | 26
  label: string
}) {
  return (
    <span role="radiogroup" aria-label={label} className="inline-flex shrink-0 rounded-[10px] bg-line-soft p-[3px]">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          onClick={() => onChange(option.value)}
          className={cn('inline-flex items-center justify-center gap-[5px] whitespace-nowrap rounded-lg px-3 text-[12px] font-semibold',
            option.value === value ? 'bg-surface text-text shadow-[0_1px_2px_rgba(20,20,10,.12)]' : 'text-text-muted hover:text-text')}
          style={{ height }}
        >
          {option.label}
        </button>
      ))}
    </span>
  )
}

/** The Inbox's pill chips — *Unread · 3* · *All*, the selected one green (Board 7: 30 px; Board 16: 32 px). */
export function FilterChip({ on, onClick, children, height = 30 }: {
  on: boolean; onClick: () => void; children: ReactNode; height?: 30 | 32
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cn('inline-flex shrink-0 items-center whitespace-nowrap rounded-full border px-3 text-[12.5px] font-semibold',
        on ? 'border-accent-line bg-accent-soft text-accent-dark' : 'border-line bg-surface text-text hover:bg-hover')}
      style={{ height }}
    >
      {children}
    </button>
  )
}

/** A button as the boards draw them: 30 or 32 high, 8 px corners, 600. */
export const BUTTON = 'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border font-semibold disabled:opacity-50'
export const PLAIN = 'border-line bg-surface text-text hover:bg-hover'
export const PRIMARY = 'border-accent bg-accent text-white hover:bg-accent-dark'

/** The uppercase section heading of the side panel (Board 7). */
export function PanelHead({ children }: { children: ReactNode }) {
  return (
    <div className="mb-2 mt-4 flex items-center text-[10.5px] font-semibold uppercase tracking-[.06em] text-text-faint">
      {children}
    </div>
  )
}

/** What went wrong, in a line. */
export function errorWords(cause: unknown): string {
  return cause instanceof Error ? cause.message : 'Something went wrong. Try again.'
}

/** The draft of one conversation, kept in this tab until it is sent (§7.1 — not state, a draft). */
export interface Draft {
  channel?: InboxChannel
  body: string
  subject?: string
  /** The chips (Aaron, Oct 26); a draft kept before then has the Cc box's text. */
  cc?: string | Copy[]
  bcc?: Copy[]
}

const draftKey = (conversationId: string): string => `rowted.inbox.draft.${conversationId}`

export function readDraft(conversationId: string): Draft | null {
  try {
    const raw = window.sessionStorage.getItem(draftKey(conversationId))
    return raw ? (JSON.parse(raw) as Draft) : null
  } catch {
    return null
  }
}

export function writeDraft(conversationId: string, draft: Draft | null): void {
  try {
    if (!draft || (!draft.body.trim() && !draft.subject?.trim() && !draft.cc?.length && !draft.bcc?.length)) {
      window.sessionStorage.removeItem(draftKey(conversationId))
    } else {
      window.sessionStorage.setItem(draftKey(conversationId), JSON.stringify(draft))
    }
  } catch {
    /* A draft that cannot be kept is still on the screen. */
  }
}
